import React, { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  addMonths,
  format,
  formatISO,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";

// Form Elements
import {
  CheckBox,
  DayPicker,
  DoctorCard,
  HourPicker,
  MonthPicker,
  Submit,
} from "../../FormElements";

// Components
import { ErrorMessage } from "../../ErrorMessage/ErrorMessage";
import { LoadingAnimation } from "../../../LoadingAnimation/LoadingAnimation";
import { LocationDropdown } from "../../FormElements/Dropdown/LocationDropdown/LocationDropdown";
import { DoctorDropdown } from "../../FormElements/Dropdown/DoctorDropdown/DoctorDropdown";
import { GenericModal } from "../../FormElements/Modal/GenericModal/GenericModal";
import { StepLayout } from "../StepLayout/StepLayout";
import { RichTextArea } from "../../RichTextArea/RichTextArea";
import { SvgIcon } from "../../../SvgIcon/SvgIcon";
import { Breadcrumbs } from "../../Breadcrumbs/Breadcrumbs";

// Types & Models
import { AppointmentDto } from "../../../../../Api/Model/AppointmentDto.csharp";
import { DoctorDto } from "../../../../../Api/Model/DoctorDto.csharp";
import { LocationDto } from "../../../../../Api/Model/LocationDto.csharp";
import { RemoteContactViewModel } from "../../../../../Api/Model/RemoteContactViewModel.csharp";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";
import { HelsebokaInfo } from "@apiModels/HelsebokaInfo.csharp";

// Helpers
import { RootDataType } from "../../Helpers/interfaces";
import { FieldStatus } from "../../Helpers/FieldStatus";
import { clear, onSubmit, setStorage } from "../../Helpers/formFunctions";
import { GtmService } from "../../GtmService";
import {
  findRelatedServiceLocationSetup,
  isRelatedServiceEnabled,
} from "../../Helpers/relatedServiceHelper";
import { newDateInOsloTimeZone } from "../../Helpers/timeZoneHelpers";
import { stepHeadings } from "../../stepHeadings";
import { nb } from "date-fns/locale";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "../../Helpers/formTypes";

// Hooks
import { useLabels } from "../../Hooks/useLabels";
import { useFilteredLocations } from "../../Hooks/useFilteredLocations";
import { useFilteredDoctors } from "../../Hooks/useFilteredDoctors";
import { useInterval } from "../../Hooks/useAppointments";
import { useFilteredAppointmentsDetails } from "../../Hooks/useAppointmentDetails";
import { useMissingAppointmentsMessage } from "../../Hooks/useNoAppointmentsMessage";
import { usePricingSummary } from "../../Hooks/usePricingSummary";
import { useTimeZoneModal } from "../../Hooks/useTimeZoneModal";
import { useCancellationDeadline } from "../../Hooks/useCancellationDeadline";
import { useMissingAppointments } from "../../Hooks/useMissingAppointments";
import { useRelatedServiceSetups } from "../../Hooks/useRelatedServiceSetups";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import { useLocationDropdown } from "../../Hooks/useLocationAndDoctorSelection";
import { LocationDropdownLegacy } from "../../FormElements/Dropdown/LocationDropdownLegacy/LocationDropdownLegacy";
import classNames from "classnames";
import { useFeatureFlags } from "../../../../Context/FeatureFlagsContext";

type AppointmentStepType = {
  state: FormStateType;
  dispatch: FormDispatch;
  nextStep: () => void;
  rootData: RootDataType;
};

export type IntervalDay = {
  hasAppointments: boolean;
  date: Date;
};

export type HelsebokaFilter = {
  defragment: boolean;
  treatmentCategoryIds: number[];
  treatmentIds: number[];
  type: "NORMAL" | "VIDEO";
};

export const AppointmentStep: FC<AppointmentStepType> = ({
  state,
  dispatch,
  nextStep,
  rootData,
}) => {
  const { bookingLabels } = useLabels();
  const { filteredLocations, unavailableLocations } = useFilteredLocations(
    rootData.centers,
    state.service
  );
  const { filteredDoctors } = useFilteredDoctors(
    rootData.doctors,
    state.service,
    state.location
  );

  const { isFeatureEnabled } = useFeatureFlags();
  const decision = isFeatureEnabled("enable_new_location_dropdown");

  const [isHelsebokaModalOpen, setIsHelsebokaModalOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useRelatedServiceSetups(state.service, dispatch);
  const { newBookingFilterValues } = useBookingContext();
  const getHelsebokaFilterValue = (
    helsebokaInfo: HelsebokaInfo
  ): HelsebokaFilter => {
    return {
      defragment: true,
      treatmentIds: [helsebokaInfo.serviceId],
      treatmentCategoryIds: [helsebokaInfo.serviceCategoryId],
      type: "NORMAL",
    };
  };
  const submitHelsebokaModal = () => {
    setIsHelsebokaModalOpen(false);

    if (!!state.appointment.helsebokaInfo) {
      const filter = getHelsebokaFilterValue(state.appointment.helsebokaInfo);
      const uri = encodeURI(
        `https://www.helseboka.app/booking/clinic/${
          state.appointment.helsebokaInfo.clinicId
        }/treatment?timeSlotId=${
          state.appointment.helsebokaInfo.appointmentId
        }&filter=${JSON.stringify(filter)}`
      );
      window.open(uri, "_blank", "noreferrer");
    }
  };
  const updateServiceAppointments = (
    value: boolean,
    relatedServiceId?: number
  ) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    /* Initial state */
    const newState = new Map<number, boolean>(state.showRelatedServicesMap);
    let newBaseFilterState = state.showBaseService;
    /* Logic */
    if (relatedServiceId !== undefined) {
      newState.set(relatedServiceId, value);
      if ([...newState].filter(([k, v]) => v).length === 0) {
        newBaseFilterState = true;
      }
    } else {
      newBaseFilterState = value;
      if (!newBaseFilterState) {
        for (let [key] of newState) {
          newState.set(key, true);
        }
      }
    }
    /* Updating form */
    setValue(FormFields.showBaseService, newBaseFilterState);
    for (let [key, value] of newState) {
      setValue(`${FormFields.showRelatedServices}-${key}`, value);
    }
    /* Updating state */
    dispatch({
      type: FormReducer.update,
      field: FormFields.showBaseService,
      payload: newBaseFilterState,
    });
    dispatch({
      type: FormReducer.update,
      field: FormFields.showRelatedServicesMap,
      payload: newState,
    });
  };

  useMissingAppointments(state.service, state.showRelatedServicesMap, dispatch);

  const [selectedMonth, setSelectedMonth] = useState<Date>(
    startOfMonth(newDateInOsloTimeZone())
  );
  const [selectedDay, setSelectedDay] = useState<Date>(
    startOfDay(newDateInOsloTimeZone())
  );

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [autoQueryRepeat, setAutoQueryRepeat] = useState(0);

  const { secondStepLabels } = bookingLabels!.stepsBookingLabels!;
  const commonLabels = bookingLabels!.commonLabels;

  const { missingAppointmentsMessage: noAppointmentMessage } =
    useMissingAppointmentsMessage(
      state.doctor,
      state.location,
      state.specializationName,
      state.service,
      state.missingAppointments,
      rootData.centers
    );

  const {
    handleSubmit,
    setValue,
    formState: { errors },
    register,
    trigger,
  } = useForm<FormStateType>({
    defaultValues: state,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const {
    onLocationSelect: baseOnLocationSelect,
    onDoctorSelect: baseOnDoctorSelect,
  } = useLocationDropdown(filteredLocations, state, dispatch, setValue);

  useEffect(() => {
    !!state.selectedMonth
      ? handleMonthChange(parseISO(state.selectedMonth))
      : handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

    !!state.selectedDay
      ? handleDayChange(parseISO(state.selectedDay))
      : handleDayChange(startOfDay(newDateInOsloTimeZone()));
  }, []);

  useEffect(() => {
    for (const rs of state.relatedServiceSetups) {
      const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
        rs,
        state.location
      );

      updateServiceAppointments(
        relatedServiceLocationSetup?.showRelatedService ??
          rs.showRelatedService,
        rs.relatedServiceId
      );
    }
  }, [state.location]);

  const updateRemoteContact = (remoteContact: RemoteContactViewModel) => {
    dispatch({
      type: FormReducer.update,
      field: FormFields.remoteContact,
      payload: remoteContact,
    });
  };

  const {
    interval,
    filteredAppointments,
    loadingAppointments,
    error: appointmentsError,
    warning: appointmentsWarning,
    failedCenters: appointmentsFailedCenters,
  } = useInterval(
    state,
    selectedMonth,
    autoQueryNextMonth,
    autoQueryRepeat,
    filteredDoctors
  );

  useEffect(() => {
    if (state.appointment.serviceVariantId) {
      setValue(FormFields.appointment, state.appointment);
      dispatch({
        type: FormReducer.update,
        field: FormFields.appointment,
        payload: state.appointment,
      });
    }
  }, []);

  const {
    filteredAppointmentsDetails,
    loadingAppointmentsDetails,
    error: appointmentsDetailsError,
    warning: appointmentsDetailsWarning,
    failedCenters: appointmentsDetailsFailedCenters,
  } = useFilteredAppointmentsDetails(
    state,
    updateRemoteContact,
    selectedDay,
    filteredDoctors
  );

  useEffect(() => {
    setStorage(state);
  }, [state]);

  useEffect(() => {
    GtmService.bookingAppointmentsScreenShown(
      state.specializationName,
      state.service.specializationId,
      state.service.bookingName,
      state.service.id,
      state.location.name,
      state.location.id,
      state.doctor.name,
      state.doctor.id,
      newBookingFilterValues?.isBookingFallback,
      newBookingFilterValues?.breadcrumbsShown && state.breadcrumbs.length > 0
    );
  }, []);

  function secondStepSubmit() {
    const center = rootData.centers.find(
      (c) => c.id === state.appointment.centerId
    );
    const doctor = rootData.doctors.find(
      (d) => d.id === state.appointment.doctorId
    );

    GtmService.appointmentStepSubmit(
      state.specializationName,
      state.service.specializationId,
      state.service.bookingName,
      state.service.id,
      center?.name ?? "",
      center?.id ?? "",
      doctor?.name ?? "",
      doctor?.id ?? "",
      state.appointment.startTime,
      newBookingFilterValues?.breadcrumbsShown
    );

    if (
      state.appointment.appointmentBookingSystem ===
      AppointmentBookingSystem.Helseboka
    ) {
      setIsHelsebokaModalOpen(true);
      return;
    }

    return onSubmit(nextStep, errors);
  }

  function handleMonthChange(newMonth: Date) {
    setAutoQueryRepeat(0);
    setSelectedMonth(newMonth);
  }

  function autoQueryNextMonth() {
    setAutoQueryRepeat(autoQueryRepeat + 1);
    setSelectedMonth(addMonths(selectedMonth, 1));
  }

  function handleDayChange(day: Date) {
    if (day.getTime() == selectedDay?.getTime()) {
      return;
    }

    setSelectedDay(day);
    clear(FormFields.appointment, dispatch, setValue);

    dispatch({
      type: FormReducer.update,
      field: FormFields.selectedDay,
      payload: formatISO(day),
    });
  }

  const onDoctorSelect = (doctor: DoctorDto) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    baseOnDoctorSelect(doctor);
  };

  const onLocationSelect = (location: LocationDto) => {
    updateMonthToCurrentMonth();
    updateDayToFirstAvailable();
    clear(FormFields.appointment, dispatch, setValue);
    baseOnLocationSelect(location);
  };

  const updateMonthToCurrentMonth = () =>
    handleMonthChange(startOfMonth(newDateInOsloTimeZone()));

  const updateDayToFirstAvailable = () => {
    if (interval.length) {
      const index = interval.findIndex(
        (day) => day.hasAppointments && day.date >= selectedMonth
      );
      if (index !== -1) {
        setSelectedDay(interval[index].date);
      }
    }
  };

  function handleTimeChoose(appointment: AppointmentDto) {
    setValue(FormFields.appointment, appointment);
    dispatch({
      type: FormReducer.update,
      field: FormFields.appointment,
      payload: appointment,
    });
  }

  usePricingSummary(state.appointment, dispatch);

  useCancellationDeadline(state.appointment, dispatch);

  useEffect(() => {
    updateDayToFirstAvailable();
  }, [interval.length]);

  useEffect(() => {
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const shouldDisplayMonthBackButton = () => {
    if (interval.length == 0) {
      return false;
    }
    const [first] = interval;
    const start = startOfMonth(first.date);
    return start.getTime() < selectedMonth.getTime();
  };

  const isLoading = loadingAppointments || loadingAppointmentsDetails;
  const hasError = !!appointmentsError || !!appointmentsDetailsError;

  const isFailedCenterSelected = appointmentsFailedCenters.some(
    (c) => c.id === state.location?.id
  );

  const warningCenters = !!appointmentsDetailsFailedCenters.length
    ? appointmentsDetailsFailedCenters
    : appointmentsFailedCenters;
  const selectedCentersIds =
    state.location.id && state.location.id != "-1"
      ? state.location.isLocation == true
        ? state.location.centers
        : [state.location.id]
      : [];

  const selectedWarningCenters = !!selectedCentersIds.length
    ? warningCenters.filter((c) => selectedCentersIds.indexOf(c.id) != -1)
    : warningCenters;
  const hasWarning =
    (!!appointmentsWarning || !!appointmentsDetailsWarning) &&
    !!selectedWarningCenters.length;

  const warningMessage = (
    appointmentsWarning || appointmentsDetailsWarning
  )?.replace("$centers", selectedWarningCenters.map((c) => c.name).join(", "));

  const areAppointmentsLoaded =
    interval.length > 0 && filteredAppointmentsDetails.length > 0;

  const { showTimeZoneModal, setShowTimezoneModal } = useTimeZoneModal(
    state,
    dispatch,
    areAppointmentsLoaded,
    hasError
  );

  const submitButtonLabel = secondStepLabels?.nextButtonLabel ?? "Neste";

  function* countdown(a: number, b: number = 1) {
    while (a >= b) {
      yield a--;
    }
  }

  const shouldShowBreadcrumbs = state.breadcrumbs.length > 0;

  const appointmentStepCustomHeading =
    bookingLabels?.stepsBookingLabels?.newBookingStepsLabels
      .appointmentsStepHeading;
  const appointmentStepHeading =
    appointmentStepCustomHeading || stepHeadings.appointmentStepHeading;

  return (
    <div
      className={classNames("AppointmentStep", {
        "AppointmentStep--isDecision": decision,
      })}
      ref={wrapperRef}
    >
      <StepLayout
        heading={appointmentStepHeading}
        headerAdditionalContent={
          shouldShowBreadcrumbs && (
            <Breadcrumbs breadcrumbs={state.breadcrumbs} />
          )
        }
      >
        <form
          onSubmit={handleSubmit(secondStepSubmit)}
          className="d-flex flex-column"
        >
          <div className="AppointmentStep__dropdownContainer">
            {decision ? (
              <LocationDropdown
                defaultValue={state.location}
                label={commonLabels.placeDropdownLabel}
                placeholder={commonLabels.placeDropdownPlaceholder}
                onSelect={onLocationSelect}
                locations={{ items: filteredLocations }}
                unavailableLocations={{ items: unavailableLocations }}
                doctor={state.doctor}
                onDoctorSelect={onDoctorSelect}
                service={state.service}
                additionalLabels={commonLabels}
              />
            ) : (
              <LocationDropdownLegacy
                defaultValue={state.location}
                label={commonLabels.placeDropdownLabel}
                placeholder={commonLabels.placeDropdownPlaceholder}
                onSelect={onLocationSelect}
                locations={{ items: filteredLocations }}
                unavailableLocations={{ items: unavailableLocations }}
                doctor={state.doctor}
                onDoctorSelect={onDoctorSelect}
                service={state.service}
                additionalLabels={commonLabels}
              />
            )}
            {state.service.disableDoctorSelection == false &&
              state.location.servicesWithDisabledDoctorSelection.indexOf(
                state.service.id
              ) == -1 && (
                <DoctorDropdown
                  defaultValue={state.doctor}
                  label={secondStepLabels.selectDoctorDropdownLabel}
                  labelSelected={
                    secondStepLabels.selectDoctorDropdownLabelSelected
                  }
                  placeholder={secondStepLabels.selectDoctorDropdownPlaceholder}
                  onSelect={onDoctorSelect}
                  doctors={filteredDoctors}
                  doctor={state.doctor}
                  status={hasError ? FieldStatus.disabled : FieldStatus.active}
                />
              )}
          </div>

          <div className="AppointmentStep__doctorCardScrollTo" />
          {state.doctor.id && state.doctor.id != "-1" && (
            <DoctorCard doctor={state.doctor} centers={rootData.centers} />
          )}

          {state.relatedServiceSetups.map((rs) => {
            const relatedServiceLocationSetup = findRelatedServiceLocationSetup(
              rs,
              state.location
            );

            return (
              isRelatedServiceEnabled(rs, state.location) && (
                <div key={rs.relatedServiceId}>
                  <h2 className="AppointmentStep__relatedServiceHeading">
                    {relatedServiceLocationSetup?.showRelatedServicesLabel ??
                      rs.showRelatedServicesLabel}
                  </h2>
                  <CheckBox
                    name={`${FormFields.showRelatedServices}-${rs.relatedServiceId}`}
                    label={
                      <>
                        {relatedServiceLocationSetup?.showRelatedServicesCheckboxLabel ??
                          rs.showRelatedServicesCheckboxLabel}
                        <SvgIcon
                          iconName="video"
                          className="AppointmentStep__relatedIcon"
                        />
                      </>
                    }
                    register={register}
                    onClick={() =>
                      updateServiceAppointments(
                        !state.showRelatedServicesMap.get(rs.relatedServiceId),
                        rs.relatedServiceId
                      )
                    }
                    disabled={hasError || isLoading}
                    trigger={trigger}
                  />
                </div>
              )
            );
          })}
          {state.service.showRelatedServiceCheckboxLabel &&
            state.relatedServiceSetups.filter((rs) =>
              isRelatedServiceEnabled(rs, state.location)
            ).length > 0 && (
              <CheckBox
                name={FormFields.showBaseService}
                label={state.service.showRelatedServiceCheckboxLabel}
                register={register}
                onClick={() =>
                  updateServiceAppointments(!state.showBaseService)
                }
                disabled={hasError || isLoading}
                trigger={trigger}
              />
            )}

          {!!noAppointmentMessage ? (
            <RichTextArea
              className={"AppointmentStep__noAppointmentMsg"}
              icon="info"
              content={noAppointmentMessage}
            />
          ) : state.service.availableForOnlineBooking ? (
            <>
              {hasError && (
                <ErrorMessage
                  className="AppointmentStep__errorMessage"
                  message={appointmentsError || appointmentsDetailsError}
                />
              )}
              {hasWarning && !hasError && (
                <ErrorMessage
                  className="AppointmentStep__errorMessage"
                  message={warningMessage}
                />
              )}
              {loadingAppointments && !hasError && !isFailedCenterSelected && (
                <LoadingAnimation
                  text={
                    autoQueryRepeat === 0
                      ? bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForCurrentMonth.replace(
                          "$currentMonth",
                          format(selectedMonth, "MMMM", { locale: nb })
                        )
                      : bookingLabels!.stepsBookingLabels.secondStepLabels.loadingAppointmentsForNextMonth
                          .replace(
                            "$currentMonth",
                            format(selectedMonth, "MMMM", { locale: nb })
                          )
                          .replace(
                            "$checkedMonths",
                            [...countdown(autoQueryRepeat)]
                              .map((value) =>
                                format(
                                  subMonths(selectedMonth, value),
                                  "MMMM",
                                  {
                                    locale: nb,
                                  }
                                )
                              )
                              .join(", ")
                          )
                  }
                  className="loading-animation-container text-center"
                />
              )}

              {!loadingAppointments && !hasError && !isFailedCenterSelected && (
                <>
                  <MonthPicker
                    currentMonth={selectedMonth}
                    handleButtonClick={handleMonthChange}
                    displayBackButton={shouldDisplayMonthBackButton()}
                  />

                  {interval.length === 0 ? (
                    <RichTextArea
                      className="AppointmentStep__noAppointmentMsg"
                      icon="info"
                      content={
                        autoQueryRepeat === 0
                          ? bookingLabels!.stepsBookingLabels.secondStepLabels
                              .noAppointments
                          : bookingLabels!.stepsBookingLabels.secondStepLabels
                              .noAppointmentsAutoQuery
                      }
                    />
                  ) : (
                    <>
                      <DayPicker
                        selectedDay={selectedDay}
                        selectedMonth={selectedMonth}
                        handleDayChange={handleDayChange}
                        handleMonthChange={handleMonthChange}
                        interval={interval}
                        currentSlideIndex={currentSlideIndex}
                        setCurrentSlideIndex={setCurrentSlideIndex}
                      />
                      {selectedDay && (
                        <HourPicker
                          availableAppointments={filteredAppointmentsDetails}
                          selectedAppointment={state.appointment}
                          selectedDay={selectedDay!}
                          loading={loadingAppointmentsDetails}
                          handleTimeChange={handleTimeChoose}
                          doctors={rootData.doctors}
                          centers={rootData.centers}
                          availableDays={filteredAppointments.map(
                            (a) => a.date
                          )}
                          goToNextAvailableDay={handleDayChange}
                          selectedDoctor={state.doctor}
                          selectedLocation={state.location}
                          selectedService={state.service}
                          relatedServiceSetups={state.relatedServiceSetups}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {isHelsebokaModalOpen && (
                <GenericModal
                  onClose={() => setIsHelsebokaModalOpen(false)}
                  onSubmit={submitHelsebokaModal}
                  isOpen={isHelsebokaModalOpen}
                  heading={
                    !!secondStepLabels?.helsebokaRedirectModalHeading
                      ? secondStepLabels.helsebokaRedirectModalHeading
                      : "Fullfør din bestilling på Helseboka"
                  }
                  description={
                    !!secondStepLabels?.helsebokaRedirectModalIntro
                      ? secondStepLabels.helsebokaRedirectModalIntro
                      : "Nettbestilling til Sandvika Nevrosenter gjøres med BankID på helseboka.app"
                  }
                  buttonLabel={
                    !!secondStepLabels?.helsebokaRedirectModalButton
                      ? secondStepLabels.helsebokaRedirectModalButton
                      : "Fortsett til Helseboka"
                  }
                />
              )}
              <Submit
                className="AppointmentStep__submitButton"
                label={secondStepLabels?.nextButtonLabel}
                status={
                  state.appointment.serviceId
                    ? FieldStatus.active
                    : FieldStatus.disabled
                }
              >
                {isLoading ? (
                  <LoadingAnimation text={submitButtonLabel} />
                ) : (
                  submitButtonLabel
                )}
              </Submit>
            </>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: noAppointmentMessage,
              }}
            />
          )}
        </form>
        <GenericModal
          onClose={() => {
            setShowTimezoneModal(false);
          }}
          isOpen={showTimeZoneModal}
          heading={secondStepLabels.timeZoneModalHeader}
          description={secondStepLabels.timeZoneModalDescription}
          buttonLabel={secondStepLabels.timeZoneModalButtonLabel}
        />
      </StepLayout>
    </div>
  );
};
